import React from 'react';
import IconButton, { IconButtonTypeMap } from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { Button } from '@mui/material';
import { getEnhancedChildren } from 'helpers/IconMenuHelper';

export type IconMenuProps = {
    iconElement?: React.ReactElement<React.ComponentProps<'div'>>;
    color?: IconButtonTypeMap['props']['color'];
    disabled?: boolean;
    small?: boolean;
    className?: string;
    ['data-testid']?: string;
};

type Props = IconMenuProps & {
    children:
        | Array<React.ReactNode>
        | React.ReactElement<React.ComponentProps<typeof MenuItem>>;
    buttonText?: string;
    tooltip?: string;
    tooltipPlacement?: TooltipProps['placement'];
    menuClassName?: string;
};

const IconMenu = ({
    buttonText = '',
    tooltip = '',
    tooltipPlacement = 'top',
    iconElement = <MoreVertIcon />,
    color = 'default',
    disabled = false,
    small = false,
    className = '',
    menuClassName = '',
    children,
    'data-testid': dataTestId,
}: Props): React.ReactElement => {
    const [isOpen, setIsOpen] = React.useState(false);
    const close = () => setIsOpen(false);
    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
    const handleClick = React.useCallback((event: React.SyntheticEvent) => {
        setIsOpen(true);
        setAnchorEl(event.currentTarget);
        event.stopPropagation();
        event.preventDefault();
    }, []);

    const iconMenuElement = tooltip ? (
        <Tooltip
            title={tooltip}
            placement={tooltipPlacement}
            key="button-element"
        >
            {iconElement}
        </Tooltip>
    ) : (
        iconElement
    );

    const iconButtonElement = (
        <IconButton
            aria-owns={isOpen ? 'icon-menu' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            color={color}
            disabled={disabled}
            disableRipple={buttonText !== ''}
            className={className}
            size={small ? 'small' : 'medium'}
            data-testid={dataTestId}
        >
            {buttonText !== '' ? (
                <Button variant="contained" sx={{ pr: '0.5rem' }}>
                    {buttonText}
                    {iconMenuElement}
                </Button>
            ) : (
                iconMenuElement
            )}
        </IconButton>
    );

    const enhancedChildren = getEnhancedChildren(children, close);

    return (
        <>
            {iconButtonElement}
            <Menu
                id="icon-menu"
                key="icon-menu"
                classes={{
                    paper: menuClassName,
                }}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                open={isOpen}
                onClose={close}
                /* with MUI5, some components have z-index up to 1400.
                Without this, the IconMenu is hidden behind other components */
                sx={{ zIndex: 1400 }}
            >
                {enhancedChildren}
            </Menu>
        </>
    );
};

export default IconMenu;
